import { SCard } from 'components/shared/s-polaris';
import SelectionCard from 'components/shared/selection-card/SelectionCard';
import useAddonEnabled from 'hooks/plan/useAddonEnabled';
import { useShopPlanInfo } from 'hooks/useShopPlanInfoStatus';
import { IntegrationModeSetting } from 'models/ppoints/settings/SettingsToEcom';
import { useTranslation } from 'react-i18next';
import { transformToCdnUrl } from 'utils/google-storage-util';
import { Feature } from '../../../../pb/subscription/v1/addon_pb';

const SELECTED_CLASSES = 'border boder-black';
export const IntegrationSelector: React.FC<IntegrationSelectorProps> = (props) => {
    const { isShopifyPlusUser, isPartnerDevelopmentUser, isLoading: shopPlanInfoIsLoading } = useShopPlanInfo();
    const { isAddonEnabled: pickUpPointEnabled } = useAddonEnabled(Feature.PICKUP_POINTS_ENABLED);
    const { isAddonEnabled: hasCheckoutMapDisplayAddon, isLoading: subscriptionLoading } = useAddonEnabled(
        Feature.CHECKOUT_MAP_DISPLAY
    );
    const { t } = useTranslation();

    const getProps = (mode: IntegrationModeSetting) => ({
        onClick: () => props.onChange(mode),
        selected: mode === props.value,
    });

    return (
        <div>
            <SelectionCard {...getProps(IntegrationModeSetting.map)}>
                <div className="flex justify-between">
                    <div className="flex-1 min-h-[138px] max-w-full md:max-w-[50%] flex flex-col justify-center p-2">
                        <div className="flex gap-2 items-center">
                            <s-heading>{t('INTEGRATION_MODE.SELECTOR.MAP.TITLE')}</s-heading>{' '}
                            <s-badge tone="info">{t('INTEGRATION_MODE.SELECTOR.RECOMMENDED_BADGE')}</s-badge>
                        </div>
                        <div className="pt-1 text-text-subdued">{t('INTEGRATION_MODE.SELECTOR.MAP.DESCRIPTION')}</div>
                    </div>
                    <div className="basis-50 shrink translate-y-4 max-md:hidden mr-10">
                        <img
                            className="-rotate-8 scale-[1.1]"
                            src={transformToCdnUrl(
                                'embedded-assets/guide-images/pickupbird_choose_your_integration_style_illustration.webp'
                            )}
                        />
                    </div>
                </div>
            </SelectionCard>
            <div className="flex flex-col md:flex-row items-stretch gap-3 pt-3">
                <div className="flex-1 grid">
                    <SelectionCard {...getProps(IntegrationModeSetting.thank_you_page_map)}>
                        <div className="flex flex-col justify-center size-full p-2">
                            <s-heading>{t('INTEGRATION_MODE.SELECTOR.THANK_YOU_PAGE_MAP.TITLE')}</s-heading>
                            <div className="text-text-subdued">
                                {t('INTEGRATION_MODE.SELECTOR.THANK_YOU_PAGE_MAP.DESCRIPTION')}
                            </div>
                        </div>
                    </SelectionCard>
                </div>
                <div className="flex-1 grid">
                    <SelectionCard
                        {...getProps(IntegrationModeSetting.checkout)}
                        disabled={!pickUpPointEnabled}
                        tooltip={
                            pickUpPointEnabled
                                ? t('INTEGRATION_MODE.SELECTOR.CHECKOUT.TOOLTIP')
                                : t('INTEGRATION_MODE.SELECTOR.CHECKOUT.TOOLTIP_PLAN_REQUIRED')
                        }
                    >
                        <div className="flex flex-col justify-center size-full p-2">
                            <s-heading>{t('INTEGRATION_MODE.SELECTOR.CHECKOUT.TITLE')}</s-heading>
                            <div className="text-text-subdued">
                                {t('INTEGRATION_MODE.SELECTOR.CHECKOUT.DESCRIPTION')}
                            </div>
                        </div>
                    </SelectionCard>
                </div>
                {isShopifyPlusUser && (
                    <div className="flex-1 grid">
                        <SelectionCard
                            {...getProps(IntegrationModeSetting.checkout_map)}
                            disabled={!hasCheckoutMapDisplayAddon}
                            tooltip={
                                !hasCheckoutMapDisplayAddon &&
                                t('INTEGRATION_MODE.SELECTOR.CHECKOUT.TOOLTIP_PICKUPBIRD_PLAN_REQUIRED')
                            }
                        >
                            <div className="flex flex-col justify-center size-full p-2">
                                <s-heading>{t('INTEGRATION_MODE.SELECTOR.CHECKOUT_MAP.TITLE')}</s-heading>
                                <div className="text-text-subdued">
                                    {t('INTEGRATION_MODE.SELECTOR.CHECKOUT_MAP.DESCRIPTION')}
                                </div>
                            </div>
                        </SelectionCard>
                    </div>
                )}
            </div>
            {props.value === IntegrationModeSetting.thank_you_page_map && (
                <div className="pt-3">
                    <SCard>
                        <s-text-field
                            label={t('SETTINGS.THANK_YOU_PAGE_MAP_INTEGRATION.GOOGLE_API_KEY_HELP')}
                            placeholder={t('SETTINGS.THANK_YOU_PAGE_MAP_INTEGRATION.GOOGLE_API_KEY_PLACEHOLDER')}
                            name="googleMapApiKey"
                            value={props.googleMapApiKey ?? ''}
                            error={props.googleMapApiKeyError}
                            onInput={(event) => props.onGoogleMapApiKeyChange?.(event.currentTarget.value)}
                        />
                    </SCard>
                </div>
            )}
        </div>
    );
};

export default IntegrationSelector;

interface IntegrationSelectorProps {
    value: IntegrationModeSetting;
    onChange(value: IntegrationModeSetting): void;
    googleMapApiKey?: string;
    onGoogleMapApiKeyChange?(value: string): void;
    googleMapApiKeyError?: string;
}
